import React, { useEffect, useState } from 'react';
import { FileText, Trash2, ArrowLeft, Pin } from 'lucide-react';
import { askApi } from '../services/api';

// Pages Claude published with the publish_page tool (Ask or the Claude-app connector).
// The Outlook add-in has no artifact panel, so Claude builds the HTML, CPx stores it, and
// the link it hands back lands here: ?page=ask-page&id=<id>. No id = your own list.
//
// ⚠️ THE IFRAME SANDBOX IS THE SECURITY. The HTML is model-written. srcDoc + sandbox
// WITHOUT allow-same-origin gives it an opaque origin, so its scripts can draw charts but
// cannot read CPx's localStorage session token or call CPx functions as the viewer. Never
// add allow-same-origin here, and never open the HTML as a blob: URL - a blob inherits
// CPx's origin and would run the page with full access to the viewer's session.
const SANDBOX = 'allow-scripts allow-popups allow-popups-to-escape-sandbox allow-modals allow-downloads';

const day = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const fmt = (d) => (d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');
const S = {
  wrap: { padding: 16, display: 'flex', flexDirection: 'column', gap: 12, height: '100%' },
  bar: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  h: { margin: 0, color: '#003b5c', display: 'flex', alignItems: 'center', gap: 8, fontSize: 20 },
  sub: { color: '#64748b', fontSize: 13 },
  btn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', color: '#003b5c', cursor: 'pointer', fontSize: 13, textDecoration: 'none' },
  frame: { flex: 1, minHeight: '75vh', width: '100%', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff' },
  row: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderBottom: '1px solid #eef2f7' },
};

export default function AskPageView() {
  const id = new URLSearchParams(window.location.search).get('id');
  const [page, setPage] = useState(null);
  const [pages, setPages] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (id) askApi('page', { id }).then(setPage).catch((e) => setErr(e.message));
    else askApi('pages').then((r) => setPages(r.pages || [])).catch((e) => setErr(e.message));
  }, [id]);

  const toggleKeep = async () => {
    try {
      const r = await askApi('page-keep', { id, keep: !page.keep });
      setPage((p) => ({ ...p, keep: r.keep }));
    } catch (e) { setErr(e.message); }
  };
  const remove = async (pid) => {
    if (!window.confirm('Delete this page? The link will stop working.')) return;
    await askApi('page-delete', { id: pid });
    setPages((list) => list.filter((p) => p.id !== pid));
  };

  if (err) return <div style={S.wrap}><p style={{ color: '#b91c1c' }}>{err}</p><a style={S.btn} href="/?page=ask-page"><ArrowLeft size={14} /> My pages</a></div>;

  if (id) {
    if (!page) return <div style={S.wrap}><p style={S.sub}>Loading page…</p></div>;
    return (
      <div style={S.wrap}>
        <div style={S.bar}>
          <h2 style={S.h}><FileText size={20} color="#dc582a" /> {page.title}</h2>
          <span style={S.sub}>Made by Claude for {page.staff_name || 'someone'} · {fmt(page.created_at)} · a snapshot, not live · {page.keep ? 'Kept' : `Deletes itself ${day(page.expires_at)}`}</span>
          <span style={{ flex: 1 }} />
          {page.mine && (
            <button style={{ ...S.btn, ...(page.keep ? { background: '#003b5c', color: '#fff', borderColor: '#003b5c' } : {}) }} onClick={toggleKeep}
              title={page.keep ? 'Kept - click to let it expire' : 'Keep this page past 30 days'}>
              <Pin size={14} /> {page.keep ? 'Kept' : 'Keep'}
            </button>
          )}
          <a style={S.btn} href="/?page=ask-page"><ArrowLeft size={14} /> My pages</a>
        </div>
        <iframe title={page.title} sandbox={SANDBOX} srcDoc={page.html} style={S.frame} />
      </div>
    );
  }

  return (
    <div style={S.wrap}>
      <div style={S.bar}>
        <h2 style={S.h}><FileText size={20} color="#dc582a" /> My pages</h2>
        <span style={S.sub}>Reports and charts Claude made for you. Ask Claude for "a chart" or "a page" and the link lands here. Pages delete themselves after 30 days unless you click Keep on the page.</span>
      </div>
      {!pages ? <p style={S.sub}>Loading…</p> : !pages.length ? <p style={S.sub}>No pages yet.</p> : (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff' }}>
          {pages.map((p) => (
            <div key={p.id} style={S.row}>
              <a href={`/?page=ask-page&id=${p.id}`} style={{ color: '#003b5c', fontWeight: 600, textDecoration: 'none', flex: 1 }}>{p.title}</a>
              <span style={S.sub}>{fmt(p.created_at)}</span>
              <span style={{ ...S.sub, minWidth: 130, textAlign: 'right' }}>{p.keep ? 'Kept' : `Deletes ${day(p.expires_at)}`}</span>
              <button style={S.btn} onClick={() => remove(p.id)} title="Delete"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
